/**
 * HEDEF: PARAFON SUNUCUSU — kasa koduyla, hesapsız (kullanıcı kararları
 * 16–17.10.2026; `claude/notlar/2026-10-17-sunucu-yedegi-kararlar.md`).
 *
 * KOD: cihazda üretilen 26 karakter (Crockford base32, 130 bit). Sunucu kodu
 * saklamaz, klasörün adı kodun özeti. Kod yalnızca ADRES: dosyalar sıradan
 * `.pfai` — parolayla ya da 24 kelimeyle açılır, sunucu açamaz.
 *
 * İstekler cihaz çekirdeğinin mahremiyet denetleyici taşıyıcısından geçer
 * (`ALLOWED_BODY_FIELDS`): arayüz bu hedef için de doğrudan `fetch` çağırmaz.
 */

import { M } from "../../dil";
import {
  KASA_KODU_ALFABESI,
  KASA_KODU_UZUNLUGU,
  KASA_SAKLAMA_AY,
  type KasaHataKodu,
  type KasaListeResponse,
  type KasaOkuResponse,
} from "@parafon/contracts/central-api";
import type { BulutHedefi, HedefKaydi } from "../tipler";
import { BaglantiGerekliHatasi } from "../yedekleyici";

/** Merkeze tek bir kasa isteği; hata `{ status, code }` taşır (merkez taşıyıcısının `CentralHttpError`ı). */
export type KasaIstegi = (yol: string, govde: Readonly<Record<string, string>>) => Promise<unknown>;

export type SunucuSecimi = { readonly yeni: true } | { readonly kod: string };

type SunucuKaydi = Extract<HedefKaydi, { tur: "parafon-sunucu" }>;

export const KOD_TANINMADI = M.yedek.hedef.kodTaninmadi;

/** Yeni kod: 26 sembol, her biri 32'lik alfabeden düzgün dağılımlı (bayt & 31). */
export function kasaKoduUret(): string {
  const b = new Uint8Array(KASA_KODU_UZUNLUGU);
  globalThis.crypto.getRandomValues(b);
  let s = "";
  for (const x of b) s += KASA_KODU_ALFABESI[x & 31];
  return s;
}

/**
 * Kullanıcının yazdığı kod: tire/boşluk atılır, büyük harfe çevrilir,
 * karışan harfler düzelir (O→0, I/L→1). Geçersizse null. Sunucudaki
 * `kanonikKod` ile aynı kural.
 */
export function kanonikKasaKodu(ham: string): string | null {
  const k = ham
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
  if (k.length !== KASA_KODU_UZUNLUGU) return null;
  for (const c of k) if (!KASA_KODU_ALFABESI.includes(c)) return null;
  return k;
}

/** Gösterim: 5'li gruplar, son grup 6 — "ABCDE-FGHJK-MNPQR-STVWX-YZ0123". */
export function kasaKoduGoster(kod: string): string {
  return [kod.slice(0, 5), kod.slice(5, 10), kod.slice(10, 15), kod.slice(15, 20), kod.slice(20)].join("-");
}

function hataKodu(e: unknown): KasaHataKodu | null {
  const c = (e as { code?: unknown } | null)?.code;
  return typeof c === "string" ? (c as KasaHataKodu) : null;
}

/** Sunucunun hata kodlarını kullanıcı cümlesine çevirir; tanınmayanı olduğu gibi bırakır. */
function anlasilir(e: unknown): unknown {
  switch (hataKodu(e)) {
    case "RATE_LIMITED":
      return new Error(M.yedek.hedef.sunucuCokIstek);
    case "KASA_TAVAN":
      return new Error(M.yedek.hedef.sunucuDolu);
    case "KLASOR_DOLU":
      return new Error(M.yedek.hedef.klasorSinir);
    case "DOSYA_BUYUK":
      return new Error(M.yedek.hedef.dosyaBuyuk);
    default:
      // Gövde sınırını API'den ÖNCE ters vekil aştıysa yanıt JSON değil (HTML 413).
      if ((e as { status?: unknown } | null)?.status === 413) {
        return new Error(M.yedek.hedef.govdeBuyuk);
      }
      return e;
  }
}

/** Kullanıcı hareketinden: yeni kod açar ya da var olan kodu doğrular. */
export async function parafonSunucuBaglan(istek: KasaIstegi, secim: SunucuSecimi): Promise<SunucuKaydi> {
  if ("kod" in secim) {
    const kod = kanonikKasaKodu(secim.kod);
    if (kod === null) throw new Error(M.yedek.hedef.kodBicimi(KASA_KODU_UZUNLUGU));
    try {
      await istek("/kasa/liste", { kod });
    } catch (e: unknown) {
      if (hataKodu(e) === "KASA_YOK") throw new Error(M.yedek.hedef.kodYok);
      throw anlasilir(e);
    }
    return { tur: "parafon-sunucu", kod };
  }
  // Çakışma (130 bit) pratikte olmaz; olursa bir kez yeni kodla.
  for (let deneme = 0; deneme < 2; deneme += 1) {
    const kod = kasaKoduUret();
    try {
      await istek("/kasa/ac", { kod });
      return { tur: "parafon-sunucu", kod };
    } catch (e: unknown) {
      if (hataKodu(e) === "KASA_VAR" && deneme === 0) continue;
      throw anlasilir(e);
    }
  }
  throw new Error(M.yedek.hedef.kodAcilamadi);
}

export function parafonSunucuHedefi(kayit: SunucuKaydi, istek: KasaIstegi): BulutHedefi {
  const kod = kayit.kod;
  async function cagir<T>(yol: string, govde: Record<string, string>): Promise<T> {
    try {
      return (await istek(yol, { kod, ...govde })) as T;
    } catch (e: unknown) {
      if (hataKodu(e) === "KASA_YOK") throw new BaglantiGerekliHatasi(KOD_TANINMADI);
      throw anlasilir(e);
    }
  }
  return {
    tur: "parafon-sunucu",
    konum: M.yedek.hedef.sunucuKonum(KASA_SAKLAMA_AY),
    hazirlik: () => Promise.resolve("hazir"),
    yenidenBaglan() {
      return Promise.reject(new Error(M.yedek.hedef.sunucuYenidenBaglanmaz));
    },
    async yaz(ad, metin) {
      await cagir("/kasa/yaz", { ad, icerik: metin });
    },
    async listele() {
      const y = await cagir<KasaListeResponse>("/kasa/liste", {});
      return y.dosyalar.map((d) => ({ ad: d.ad, degisti: d.degisti, boyut: d.boyut }));
    },
    async oku(ad) {
      return (await cagir<KasaOkuResponse>("/kasa/oku", { ad })).icerik;
    },
    async sil(ad) {
      await cagir("/kasa/sil", { ad });
    },
    kayit: () => kayit,
  };
}

/**
 * KOD YENİLEME (kullanıcı kararı 16.10.2026): yeni kod = yeni klasör. Sıra:
 * yeni klasör açılır → bütün dosyalar (öbür cihazlarınkiler de) kopyalanır →
 * çağıran yeni kodu cihaza kaydeder → `eskiKasayiKapat` eski klasörü siler.
 * Yarıda kesilirse eski klasör yerinde kalır; artık yeni klasör 7 ay
 * kuralıyla kendiliğinden gider.
 */
export async function yeniKasayaKopyala(istek: KasaIstegi, eski: SunucuKaydi): Promise<SunucuKaydi> {
  const yeni = await parafonSunucuBaglan(istek, { yeni: true });
  const eh = parafonSunucuHedefi(eski, istek);
  const yh = parafonSunucuHedefi(yeni, istek);
  for (const d of await eh.listele()) await yh.yaz(d.ad, await eh.oku(d.ad));
  return yeni;
}

export async function eskiKasayiKapat(istek: KasaIstegi, eski: SunucuKaydi): Promise<void> {
  try {
    await istek("/kasa/kapat", { kod: eski.kod });
  } catch (e: unknown) {
    if (hataKodu(e) !== "KASA_YOK") throw anlasilir(e);
  }
}
